/**
 * Watches how smoothly the page is drawn while a background moves (SpaceBackground.tsx,
 * SeaBackground.tsx): when most frames come late for a while, the device is struggling and the
 * background should do less. The screen draws 60 (or more) frames a second, 30 on an iPhone
 * saving power: only frames much slower than that count as late.
 */

/** A frame is late when it comes this long after the one before (about 22 frames a second). */
export const LATE_FRAME_MS = 45;
/** How many frames are judged together. */
const WINDOW = 60;
/** A longer gap is a pause (the page was hidden, the device asleep), not a slow frame. */
const PAUSE_MS = 500;

export class FrameWatch {
  private readonly gaps: number[] = [];
  private previous = -1;

  /**
   * Call on every animation frame with its time. True once the latest frames were mostly late;
   * the watch then starts over, so that the background can do less and be judged again.
   */
  struggling(now: number): boolean {
    const gap = this.previous < 0 ? -1 : now - this.previous;
    this.previous = now;
    if (gap < 0 || gap > PAUSE_MS) return false;
    this.gaps.push(gap);
    if (this.gaps.length < WINDOW) return false;
    const sorted = [...this.gaps].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    this.gaps.length = 0;
    return median > LATE_FRAME_MS;
  }

  /** Forget the frames so far (after a pause, or a change in what is drawn). */
  reset(): void {
    this.gaps.length = 0;
    this.previous = -1;
  }
}
